import {RsnDialog, RsnMessage, useBoolean} from '@raison/ui-kit';
import * as Sentry from '@sentry/react';
import React, {Suspense, useContext, useMemo} from 'react';
import {UNSAFE_NavigationContext as NavigationContext} from 'react-router';
import {AlertHandler, IAlert} from '../model/IAlert';
import {AlertModeContext, AlertPane} from './AlertPane';

export function AlertListWrapper({
    messagesTitle,
    alerts,
    handlers,
}: {
    messagesTitle?: string;
    alerts: IAlert[];
    handlers: Record<string, AlertHandler<any>>;
}) {
    const alertElements: React.JSX.Element[] = [];
    let key = 0;
    for (const alert of alerts) {
        key += 1;
        const partialHandler = handlers[alert.subject];
        if (!partialHandler) {
            continue;
        }
        const handler = {
            ...fallbacksForHandlerOptionals,
            ...partialHandler,
        };
        alertElements.push(
            <Sentry.ErrorBoundary key={key + alert.subject} fallback={({error}) => <handler.Catcher error={error} />}>
                <Suspense fallback={<handler.Awaiter />}>
                    <handler.Manager content={alert.content}>
                        {content => <handler.Display content={content} />}
                    </handler.Manager>
                </Suspense>
            </Sentry.ErrorBoundary>
        );
    }

    return <AlertsSectionView messagesTitle={messagesTitle}>{alertElements}</AlertsSectionView>;
}

const fallbacksForHandlerOptionals = {
    Catcher: AlertPane.NoopCatcher,
    Awaiter: AlertPane.Skeleton,
};

function AlertsSectionView({
    messagesTitle = 'Important messages',
    children: alertElements,
}: {
    messagesTitle?: string;
    children: React.JSX.Element[];
}) {
    const [alertsDialogOpened, setAlertsDialogOpened] = useBoolean();

    if (alertElements.length === 0) {
        return null;
    }
    if (alertElements.length === 1) {
        return <AlertModeContext.Provider value='singular'>{alertElements[0]}</AlertModeContext.Provider>;
    }
    return (
        <AlertModeContext.Provider value={alertsDialogOpened ? 'multiple-visible' : 'multiple-hidden'}>
            <RsnMessage.Notification onClick={setAlertsDialogOpened.on}>
                <span>{messagesTitle}</span>
                <span className='float-end'>({alertElements.length})</span>
            </RsnMessage.Notification>

            <RsnDialog.Controller onClose={setAlertsDialogOpened.off} isOpen={alertsDialogOpened}>
                <AlertsDialog title={messagesTitle}>{alertElements}</AlertsDialog>
            </RsnDialog.Controller>
        </AlertModeContext.Provider>
    );
}

function AlertsDialog({title, children}: React.PropsWithChildren<{title: string}>) {
    const {onClose} = RsnDialog.useControls();

    return (
        <PatchedNavigationContext onNavigate={onClose}>
            <RsnDialog.Window>
                <RsnDialog.LeftHeader>{title}</RsnDialog.LeftHeader>
                <RsnDialog.Body padding='none'>{children}</RsnDialog.Body>
            </RsnDialog.Window>
        </PatchedNavigationContext>
    );
}

function PatchedNavigationContext({onNavigate, children}: React.PropsWithChildren<{onNavigate: () => void}>) {
    const navigation = useContext(NavigationContext);
    const patched = useMemo((): React.ContextType<typeof NavigationContext> => {
        const {navigator, ...restOfNavigation} = navigation;
        return {
            ...restOfNavigation,
            navigator: {
                ...navigator,
                push(...args) {
                    onNavigate();
                    return navigator.push(...args);
                },
                replace(...args) {
                    onNavigate();
                    return navigator.replace(...args);
                },
            },
        };
    }, [navigation, onNavigate]);

    return <NavigationContext.Provider value={patched}>{children}</NavigationContext.Provider>;
}
